import { keepPreviousData } from '@tanstack/react-query';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { memberQueryKeys } from 'TeamModule/query/membersQueryKeys';
import { MemberImpersonationItem } from 'TeamModule/api/membersImpersonationApi';
import { useMemo } from 'react';
import type { MemberSearchItem } from 'TeamModule/api/searchMembersByFieldsApi';
import * as TeamServices from 'TeamModule/services';
import * as ProfileServices from 'ProfileModule/services';
import ApiClientManager from 'core/apiClient';

const normalizeEmail = (e: string) => (e || '').trim().toLowerCase();

/**
 * Fetches impersonation data (account IDs, team membership IDs, tags) for the
 * current page of members in a single React Query call.
 *
 * Inside the queryFn two types of fetch happen in parallel:
 *  1. Team tags — one batch call per unique tenant+realm group
 *  2. Team memberships — one call per unique team (parallel)
 *
 * Results are combined into a Map<memberId, MemberImpersonationItem> that the
 * table cells use to render the Impersonate button.
 */
export const useMembersImpersonationQuery = (members: MemberSearchItem[]) => {
  const enabled = members.length > 0;

  // Deduplicate teams + build stable cache keys
  const { teams, membersKey } = useMemo(() => {
    const teamsMap = new Map<string, { tenant: string; realmId: string; teamId: string }>();
    for (const m of members) {
      const key = `${m.tenant}|${m.realmId}|${m.teamId}`;
      if (!teamsMap.has(key)) {
        teamsMap.set(key, { tenant: m.tenant, realmId: m.realmId, teamId: m.teamId });
      }
    }
    return {
      teams: Array.from(teamsMap.values()),
      membersKey: members
        .map((m) => m.id)
        .sort()
        .join(';'),
    };
  }, [members]);

  const { data: queryData, ...query } = useAppQuery<{
    data: MemberImpersonationItem[];
  }>({
    queryKey: memberQueryKeys.list({
      identifier: 'useMembersImpersonationQuery',
      memberIdsKey: membersKey,
    }),
    queryFn: async ({ token }) => {
      // Group teams by tenant+realm for batch tag fetches
      const groups = new Map<string, { tenant: string; realmId: string; teamIds: number[] }>();
      for (const t of teams) {
        const gk = `${t.tenant}|${t.realmId}`;
        if (!groups.has(gk)) {
          groups.set(gk, { tenant: t.tenant, realmId: t.realmId, teamIds: [] });
        }
        const tid = parseInt(t.teamId, 10);
        if (!Number.isNaN(tid)) groups.get(gk)!.teamIds.push(tid);
      }

      const tagsByTeamKey = new Map<string, string[]>();
      const accountsByKey = new Map<
        string,
        {
          account_id: number;
          team_membership_id?: number;
          role_name?: string;
          login_enabled: boolean | null;
        }
      >();

      await Promise.all([
        // 1. Fetch tags — one batch call per tenant+realm group
        Promise.allSettled(
          Array.from(groups.values()).map(async ({ tenant, realmId, teamIds }) => {
            if (teamIds.length === 0) return;
            const resp = await TeamServices.getTeams(
              {
                params: { limit: teamIds.length, ids: teamIds },
                headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }),
              },
              token,
            );
            const teamsData: Array<{ id: number; tags?: string[] }> =
              (resp?.data as any)?.teams || [];
            for (const t of teamsData) {
              tagsByTeamKey.set(`${tenant}|${realmId}|${t.id}`, t.tags || []);
            }
          }),
        ),

        // 2. Fetch team memberships — one call per team, all in parallel
        Promise.allSettled(
          teams.map(async ({ tenant, realmId, teamId }) => {
            const tid = parseInt(teamId, 10);
            if (Number.isNaN(tid)) return;
            const resp = await ProfileServices.getTeamMember(
              tid,
              {
                params: { include_discarded: true },
                headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }),
              },
              token,
            );
            const teamMembers: Array<{
              email: string;
              account_id: number;
              team_membership_id?: number;
              role_name?: string;
              login_enabled?: boolean | null;
            }> = (resp?.data as any)?.team_members || [];
            for (const tm of teamMembers) {
              if (tm.email && tm.account_id) {
                // Key by tenant|realm|teamId|email for unique member lookup
                accountsByKey.set(`${tenant}|${realmId}|${teamId}|${normalizeEmail(tm.email)}`, {
                  account_id: tm.account_id,
                  team_membership_id: tm.team_membership_id,
                  role_name: tm.role_name,
                  login_enabled: tm.login_enabled ?? null,
                });
              }
            }
          }),
        ),
      ]);

      // 3. Build impersonation items by matching members to fetched data
      const items: MemberImpersonationItem[] = [];
      for (const member of members) {
        const memberKey = `${member.tenant}|${member.realmId}|${member.teamId}|${normalizeEmail(member.email)}`;
        const account = accountsByKey.get(memberKey);
        if (!account) continue;
        const tagKey = `${member.tenant}|${member.realmId}|${member.teamId}`;
        items.push({
          memberId: member.id,
          accountId: account.account_id,
          teamMembershipId: account.team_membership_id,
          roleName: account.role_name,
          loginEnabled: account.login_enabled,
          tags: tagsByTeamKey.get(tagKey) || [],
        });
      }

      return { data: items };
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  });

  const impersonationMap = useMemo(() => {
    const map = new Map<string, MemberImpersonationItem>();
    const items = queryData?.data;
    if (items && Array.isArray(items)) {
      for (const item of items) {
        map.set(item.memberId, item);
      }
    }
    return map;
  }, [queryData]);

  return {
    impersonationMap,
    isLoadingImpersonation: query.isLoading || query.isFetching,
    isImpersonationError: query.isError,
  };
};

export default useMembersImpersonationQuery;
